import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useCallback, useMemo } from 'react'
import { updateTimeDelay } from 'api/harvest'
import { FormControlLabel, TextField } from '@mui/material'
import { closeModal } from 'actions/app'
import ModalTool from '../../modal/modal-tool'
import { TEST_IDS } from 'test-ids'

// The range the TRX processor accepts for a processing delay
// (ProcessingDelayValidate in farmtrx-py).
export const MIN_TIME_DELAY_SECONDS = 0
export const MAX_TIME_DELAY_SECONDS = 30

const WHOLE_NUMBER = /^\d+$/

/**
 * The entered time delay as a whole number of seconds, or null when the entry
 * isn't one or is outside the range the processor accepts.
 */
export const parseTimeDelay = (value) => {
  const trimmed = String(value ?? '').trim()
  if (!WHOLE_NUMBER.test(trimmed)) {
    return null
  }
  const seconds = Number(trimmed)
  return seconds >= MIN_TIME_DELAY_SECONDS && seconds <= MAX_TIME_DELAY_SECONDS
    ? seconds
    : null
}

export default function ChangeTimeDelay() {
  const [val, setVal] = useState('')
  const [fieldChanged, setFieldChanged] = useState(false)
  const [equipmentChoice, setEquipment] = useState()

  const harvest = useSelector(
    (state) => state.harvest.current && state.harvest.current,
  )

  const dispatch = useDispatch()

  const { t } = useTranslation()

  const closePopup = useCallback(() => {
    dispatch(closeModal())
  })

  const onChange = useCallback((e) => {
    setVal(e.target.value)
    setFieldChanged(true)
  }, [])

  const timeDelaySeconds = parseTimeDelay(val)
  const showInvalid = fieldChanged && timeDelaySeconds === null

  let harvesterInfo = harvest?.stats?.harvester_info

  const hardware = useSelector((state) => state.hardware.collection)
  const equipment = useSelector((state) => state.equipment.collection)

  let equipmentArray = useMemo(() => {
    let _equipmentArray = []
    if (hardware && equipment && harvesterInfo) {
      for (let harvester in harvesterInfo) {
        const h = harvesterInfo[harvester]
        //if harvesterInfo has a ymId use it, if it doesn't then try to use whatever harvester info is indexed by.
        const ymId = h?.ymId || harvester
        let hwObj = hardware.find((hw) => hw?.farmtrxHardwareId === ymId)
        let eqObj =
          hwObj && equipment.find((eq) => eq.id === hwObj?.equipmentId)
        _equipmentArray.push({
          name: eqObj ? eqObj.nickname : ymId,
          ymId: ymId,
        })
      }
    }
    return _equipmentArray
  }, [hardware, equipment, harvesterInfo])

  // Derived rather than assigned during render: falling back to the first
  // entry keeps the shown value and the submitted value in agreement.
  const selectedEquipment = equipmentChoice ?? equipmentArray[0]?.ymId

  const onAChangeTimeDelay = useCallback(() => {
    dispatch(
      updateTimeDelay(
        harvest.id,
        timeDelaySeconds,
        selectedEquipment,
        harvest.tenantId,
      ),
    )
  }, [harvest, timeDelaySeconds, selectedEquipment])

  const onChangeEquipment = useCallback((e) => {
    setEquipment(e.target.value)
  }, [])

  return (
    <ModalTool
      header={t('edit_time_delay')}
      content={
        <>
          <FormControlLabel
            control={
              <TextField
                id="select-equipment"
                sx={{ width: '25ch', padding: '10px' }}
                disabled={equipmentArray.length < 1}
                select
                name="equipment"
                value={selectedEquipment}
                onChange={onChangeEquipment}
                slotProps={{
                  select: { native: true },
                  htmlInput: {
                    'data-testid': TEST_IDS.timeDelayEquipmentSelect,
                  },
                }}
                variant="outlined"
                size="small"
                placeholder={t('select_equipment')}
              >
                {equipmentArray.map((equipment) => (
                  <option value={equipment.ymId} key={equipment.ymId}>
                    {equipment.name}
                  </option>
                ))}
              </TextField>
            }
            label={t('equipment')}
            labelPlacement="start"
          />
          <FormControlLabel
            control={
              <TextField
                id="changeTimeDelay"
                sx={{ width: '25ch', padding: '10px' }}
                value={val}
                name="changeTimeDelay"
                variant="outlined"
                size="small"
                type="number"
                error={showInvalid}
                helperText={showInvalid ? t('invalid_time_delay') : ''}
                onChange={onChange}
                slotProps={{
                  htmlInput: {
                    min: MIN_TIME_DELAY_SECONDS,
                    max: MAX_TIME_DELAY_SECONDS,
                    step: 1,
                    'data-testid': TEST_IDS.timeDelayInput,
                  },
                }}
              />
            }
            label={t('time_delay')}
            labelPlacement="start"
          />
        </>
      }
      onClose={closePopup}
      onApply={onAChangeTimeDelay}
      disabled={timeDelaySeconds === null || !selectedEquipment}
    />
  )
}
